<template>
  <b-modal
    title="Update Account"
    ref="update-account-modal"
    id="update-account-modal"
    size="lg"
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
  >
    <validation-observer ref="checkInfoRules" tag="form">
      <b-card class="m-0">

        <!-- title -->
        <validation-provider
          #default="{ errors }"
          name="Title"
          rules="required"
        >
          <b-form-group label-for="title">
            Title<span class="text-danger"> *</span>
            <b-form-input
              id="title"
              class="form-control"
              :class="{
                'text-danger': accountInfo.title.length > titleSize,
                'border-danger': accountInfo.title.length > titleSize,
              }"
              v-model="accountInfo.title"
              maxLength="50"
            />

            <small
              class="textarea-counter-value float-right"
              :class="{
                'bg-danger': accountInfo.title.length > titleSize,
              }"
            >
              <span class="char-count">{{ accountInfo.title.length }}</span> /
              {{ titleSize }}
            </small>
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>


        <!-- description -->
        <b-form-group label-for="description" label="Description">
          <b-form-textarea
            id="description"
            v-model="accountInfo.description"
            rows="3"
            maxLength="200"
          />
          <small
            class="textarea-counter-value float-right"
            :class="{
              'bg-danger': accountInfo.description.length > descriptionSize,
            }"
          >
            <span class="char-count">{{
              accountInfo.description.length
            }}</span>
            / {{ descriptionSize }}
          </small>
        </b-form-group>

        
        <!-- price -->
        <validation-provider
          #default="{ errors }"
          name="Creative Groups"
          rules="required"
        >
          <b-form-group label-for="price">
            Price<span class="text-danger"> *</span>
            <vue-select
              id="price"
              v-if="hasSuperAdminRole()"
              v-model="accountInfo.priceId"
              :options="priceOptions"
              :reduce="(option) => option.id"
              label="title"
            />
            <vue-select
              id="price"
              v-if="hasAdminRole()"
              v-model="accountInfo.priceId"
              :options="priceOptions"
              :reduce="(option) => option.id"
              :disabled="disabled"
              :readonly="readonly"
              label="title"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>


        <b-row>
          <!-- price start date -->        
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Creative Groups"
              rules="required"
            >
              <b-form-group label-for="price-start-date">
                Price Start Date<span class="text-danger"> *</span>
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="accountInfo.priceStartDt"
                    id="start_date"
                    v-if="hasSuperAdminRole()"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                  <flat-pickr
                    v-model="accountInfo.priceStartDt"
                    id="start_date"
                    v-if="hasAdminRole()"
                    :config="dateConfig"
                    :disabled="disabled"
                    :readonly="readonly"
                    classes="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
          <!-- price end date -->
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Creative Groups"
              rules="required"
            >
              <b-form-group label-for="price-end-date">
                Price End Date<span class="text-danger"> *</span>
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="accountInfo.priceEndDt"
                    id="end_date"
                    v-if="hasSuperAdminRole()"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                  <flat-pickr
                    v-model="accountInfo.priceEndDt"
                    id="end_date"
                    v-if="hasAdminRole()"
                    :config="dateConfig"
                    :disabled="disabled"
                    :readonly="readonly"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row>
          <!-- auto extension -->
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Creative Groups"
              rules="required"
            >
              <b-form-group label-for="price">
                Auto Extension<span class="text-danger"> *</span>
                <vue-select
                  id="auto-extension"
                  v-if="hasSuperAdminRole()"
                  v-model="accountInfo.autoExtension"
                  :options="autoExtensionOptions"
                  :reduce="(option) => option.id"
                  label="title"
                />
                <vue-select
                  id="auto-extension"
                  v-if="hasAdminRole()"
                  v-model="accountInfo.autoExtension"
                  :options="autoExtensionOptions"
                  :reduce="(option) => option.id"
                  :disabled="disabled"
                  :readonly="readonly"
                  label="title"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
          <!-- extension num -->
          <b-col>
              <b-form-group label-for="extensionNum">
                Extension Number
                <b-form-input
                  id="title"
                  v-if="hasSuperAdminRole()"
                  class="form-control"
                  v-model="accountInfo.extensionNum"
                  type="number"
                  @keypress="checkNumber"
                />
                <b-form-input
                  id="title"
                  v-if="hasAdminRole()"
                  class="form-control"
                  v-model="accountInfo.extensionNum"
                  type="number"
                  :disabled="disabled"
                  :readonly="readonly"
                />
              </b-form-group>
          </b-col>
        </b-row>


        <!-- categories -->
        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Creative Groups"
              rules="required"
            >
              <b-form-group label-for="categories">
                Basic Categories<span class="text-danger"> *</span> <span style="margin-left: 60rem;">{{accountInfo.categories.length}} / {{account.maxCateNum}}</span>
                <vue-select
                  id="ctgId"
                  v-model="accountInfo.categories"
                  :options="ctgOptions"
                  :multiple="true"                  
                  @input="categoryLimit"
                  label="title"
                  :close-on-select="false"
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>










        <b-row class="my-1">
          <b-col md="12">
            Extra Categories
            <feather-icon
              variant="primary"
              v-if="hasSuperAdminRole()"
              icon="PlusCircleIcon"
              class="cursor-pointer ml-1"
              size="18"
              ref="btn"
              @click="addExtraCategory"
            />
          </b-col>
        </b-row>
        <b-form
          ref="form"
          v-if="hasSuperAdminRole()"
          class="repeater-form"
          @submit.prevent="addExtraCategory"
        >
          <b-row
            v-for="(item, index) in accountInfo.extraCategories"
            :key="index"
            ref="row"
            class="d-flex align-items-center"
          >
            <b-col md="5">
              <b-form-group label-for="categoryNm" class="ml-2">
                Category
                <vue-select
                  id="ctgId"
                  v-model="item.ctgNm"
                  :options="ctgOptions"
                  label="title"
                  @input="selectExtraCategory(item, $event)"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                Start Date
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="item.relStartDt"
                    id="start_dt"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                End Date
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="item.relEndDt"
                    id="start_dt"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                Price(만원)
                <b-form-input
                  v-model="item.relPrice"
                  type="number"
                  @keypress="checkNumber"
                />
              </b-form-group>
            </b-col>
            <b-col md="1">
              <feather-icon
                variant="primary"
                icon="MinusCircleIcon"
                class="cursor-pointer"
                size="18"
                ref="btn"
                @click="removeExtraCategory(index)"
              />
            </b-col>
          </b-row>
        </b-form>

        <b-form
          ref="form"
          v-if="hasAdminRole()"
          class="repeater-form"
          @submit.prevent="addExtraCategory"
        >
          <b-row
            v-for="(item, index) in accountInfo.extraCategories"
            :key="index"
            ref="row"
            class="d-flex align-items-center"
          >
            <b-col md="5">
              <b-form-group label-for="categoryNm" class="ml-2">
                Category
                <vue-select
                  id="ctgId"
                  v-model="item.ctgNm"
                  :options="ctgOptions"
                  label="title"
                  :disabled="disabled"
                  :readonly="readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                Start Date
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="item.relStartDt"
                    id="start_dt"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                    :disabled="disabled"
                    :readonly="readonly"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                End Date
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="item.relEndDt"
                    id="start_dt"
                    :config="dateConfig"
                    class="form-control flat-picker"
                    placeholder="Mon DD, YYYY"
                    :disabled="disabled"
                    :readonly="readonly"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label-for="categoryDt">
                Price(만원)
                <b-form-input
                  v-model="item.relPrice"
                  type="number"
                  :disabled="disabled"
                  :readonly="readonly"
                />
              </b-form-group>
            </b-col>
            <b-col md="1">
            </b-col>
          </b-row>
        </b-form>
        




        
        
      </b-card>

      <b-button
        class="mr-50 mt-50 float-right"
        variant="primary"
        @click="success"
      >
        Done
      </b-button>
      <b-button
        class="mr-1 mt-50 float-right"
        variant="outline-secondary"
        @click="cancel"
      >
        Cancel
      </b-button>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BInputGroup,
  BFormInvalidFeedback,
  BInputGroupPrepend,
  BInputGroupAppend,
  BCol,
  BRow,
  BForm,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import CISAccountService from "@/services/cis-account.service";

import FlatPickr from "vue-flatpickr-component";

export default {
  name: "UpdateAccountModal",
  components: {
    BCard,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BInputGroup,
    BFormInvalidFeedback,
    BInputGroupPrepend,
    BInputGroupAppend,
    vSelect,
    FlatPickr,
    ValidationProvider,
    ValidationObserver,
    BCol,
    BRow,
    BForm,
  },
  props: {
    account: {
      type: Object,
      default: {},
    },
    refreshData: {
      type: Function,
      default: () => {},
    },
    prevPrice: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {

      accountInfo: { ...this.account },

      titleSize: 50,
      descriptionSize: 200,

      priceOptions: [],

      dateConfig: {
        altFormat: "M j, Y",
        altInput: true,
        minDate: this.mode !== null ? "" : new Date(),
        maxDate: "",
      },
      state: 'disabled',

      autoExtensionOptions: [
        {
          id: "Y",
          title: "Y",
        },
        {
          id: "N",
          title: "N",
        },
      ],

      ctgOptions: [],
      
    };
  },
  methods: {
    addExtraCategory() {
      this.accountInfo.extraCategories.push({
        adacctSq: this.account.adacctSq,
        ctgNm: "",
        relEndDt: "",
        relStartDt: "",
        relPrice: 0,
        relType: "extra"
      });
    },
    removeExtraCategory(index) {
      this.accountInfo.extraCategories.splice(index, 1);
    },


    async setPriceOptions(){
      const getPriceRes = await CISAccountService.priceList();
      if (getPriceRes.data.success) {
        this.priceOptions = getPriceRes.data.data;
      }
    },
    async setCtgOptions(){
      const getCtgRes = await CISAccountService.ctgList();
      if (getCtgRes.data.success) {
        this.ctgOptions = getCtgRes.data.data;
      }
    },

    checkNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    categoryLimit(e){
      console.log('-- category limit --')
      if(e.length > this.account.maxCateNum) {
        console.log('limit maxCateNum', e)
        e.pop()
      }
      // this.account.nowCateNum = this.accountInfo.categories.length
    },

    selectExtraCategory(item, e){
      console.log('-- selectExtraCategory --')
      item.adacctSq = this.accountInfo.id
      item.ctgSq = item.ctgNm.id
      item.ctgNm = item.ctgNm.title
    },




    refreshAccountInfo(account){
      this.accountInfo = account;
    },

    checkInfo() {
      return new Promise((resolve, reject) => {
        this.$refs.checkInfoRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },
    cancel() {
      this.$bvModal.hide("update-account-modal");
      this.$parent.refreshAccounts(this.accountInfo.id)
      // this.accountInfo = { ...this.account }
    },
    async success() {
      console.log('-- success --')
      console.log(this.prevPrice)
      var prevAccountInfo = this.account
      this.accountInfo.prevPriceId = this.prevPrice
      console.log(this.accountInfo)

      await this.checkInfo();
      this.$swal({
        title: "Are you sure?",
        text: `You want to update the account!`,
        icon: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await CISAccountService.updateAccount(this.accountInfo);
          if (response && response.status === 200) {
            this.$bvModal.hide("update-account-modal");
            this.refreshData();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `Successfully updated!`,
                icon: "EditIcon",
                variant: "success",
              },
            });
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: response.data.message || "Unknown error",
                icon: "AlertCircleIcon",
                variant: "danger",
              },
            });
          }
        }
      });


    },


  },
  mounted() {
    this.$root.$on("refresh-accountinfo", (account) => {
      this.refreshAccountInfo(account);
    });
  },
  created(){
    this.setPriceOptions();
    this.setCtgOptions();
  },
  computed: {
    disabled() {
        return this.state === 'disabled'
      },
    readonly() {
      return this.state === 'readonly'
    }
  },
  watch: {

  }
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.flat-picker {
  width: 263px;
}

.flatpickr-input ~ {
  padding-left: 1rem;
}

.flatpickr-input ~ .form-control[disabled] {
  background-color: #efefef;
  padding-left: 1rem;
}

</style>
